/**
 * ============================================================================
 * usePayoutRequest - PAYOUT REQUEST MUTATION HOOK
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. THIS IS A MUTATION, NOT A QUERY
 *    - useMutation for actions that change data (POST, PUT, DELETE)
 *    - useQuery for reading data (GET)
 *    - Payout request creates data, so it's a mutation
 *
 * 2. CACHE INVALIDATION IS CRITICAL
 *    - After successful payout, earnings data is stale
 *    - Must invalidate ['earnings'] cache to refetch fresh data
 *    - onSuccess callback handles this automatically
 *    - Available balance will decrease, held balance may change
 *
 * 3. ERROR HANDLING WITH RATE LIMITING
 *    - Backend returns 429 status for rate limiting
 *    - We detect this and format special error message
 *    - Include retry time so user knows when to try again
 *    - NEVER show raw error messages to users
 *
 * 4. OPTIMISTIC UPDATES - NOT USED HERE
 *    - We could optimistically update available_balance
 *    - But payout is critical financial action
 *    - Better to wait for server confirmation
 *    - Don't add optimistic updates without careful consideration
 *
 * 5. CALLBACKS ARE OPTIONAL
 *    - onSuccess, onError callbacks passed from component
 *    - Hook handles cache invalidation automatically
 *    - Component handles UI (showing modals, etc.)
 *    - Clean separation of concerns
 *
 * ============================================================================
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { vendorApi } from '../services/api';
import type { PayoutRequestResult } from '../types/revenue';

interface UsePayoutRequestOptions {
  onSuccess?: (data: PayoutRequestResult) => void;
  onError?: (error: PayoutRequestResult) => void;
}

/**
 * Mutation hook for requesting payout
 *
 * @param venueId - The venue ID to request payout for
 * @param options - Optional success/error callbacks
 * @returns TanStack Query mutation result
 *
 * @example
 * ```typescript
 * const { mutate: requestPayout, isPending } = usePayoutRequest(venue?.id, {
 *   onSuccess: (result) => {
 *     setPayoutResult(result);
 *     setShowModal(true);
 *   },
 *   onError: (error) => {
 *     setPayoutResult(error);
 *     setShowModal(true);
 *   },
 * });
 *
 * // In button click handler
 * <button onClick={() => requestPayout()} disabled={isPending}>
 *   {isPending ? 'Processing...' : 'Request Withdrawal'}
 * </button>
 * ```
 */
export function usePayoutRequest(
  venueId: number | undefined,
  options: UsePayoutRequestOptions = {}
) {
  const queryClient = useQueryClient();
  const { onSuccess, onError } = options;

  return useMutation<PayoutRequestResult, Error, void>({
    // Mutation function - the actual API call
    mutationFn: async () => {
      if (!venueId) {
        throw new Error('Venue ID is required');
      }

      const result = await vendorApi.requestPayout(venueId);
      return result;
    },

    // On success: Invalidate earnings cache and call component callback
    onSuccess: (data) => {
      // Invalidate earnings cache so next fetch gets fresh data
      // This ensures available_balance updates immediately
      queryClient.invalidateQueries({
        queryKey: ['earnings', venueId],
      });

      // Also invalidate payout history if we have it cached
      queryClient.invalidateQueries({
        queryKey: ['payoutHistory', venueId],
      });

      // Format success result
      const result: PayoutRequestResult = {
        success: true,
        amount: (data as any).net_payout || (data as any).amount,
        estimated_arrival: (data as any).estimated_arrival || 'Within 24 hours',
        reference: (data as any).transfer_reference || (data as any).reference,
      };

      // Call component's success callback if provided
      onSuccess?.(result);
    },

    // On error: Format error message and call component callback
    onError: (error: any) => {
      // Check if it's a rate limit error (429 status)
      const isRateLimit = error.response?.status === 429;
      const retryAfter = error.response?.headers?.['retry-after'];

      let result: PayoutRequestResult;

      if (isRateLimit) {
        // Format rate limit error with retry information
        const retrySeconds = parseInt(retryAfter || '60');
        const retryDate = new Date(Date.now() + retrySeconds * 1000);

        result = {
          success: false,
          error: `Too many payout requests. Please try again in ${retrySeconds} seconds.`,
          isRateLimit: true,
          retryAfter: retrySeconds,
          retryAt: retryDate.toLocaleTimeString(),
        };
      } else {
        // Format general error
        result = {
          success: false,
          error: error.response?.data?.message || error.message || 'Failed to request payout',
        };
      }

      // Call component's error callback if provided
      onError?.(result);
    },

    // Retry configuration
    retry: false, // Don't retry payout requests automatically (financial action)
  });
}

/**
 * Hook to fetch available bookings (for modal)
 * Separate from payout request because it's a query, not mutation
 */
export function useAvailableBookings(venueId: number | undefined, enabled: boolean = false) {
  return useMutation({
    mutationFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');
      return vendorApi.getEligibleBookings(venueId);
    },
  });
}

/**
 * Hook to fetch held bookings (for modal)
 */
export function useHeldBookings(venueId: number | undefined, enabled: boolean = false) {
  return useMutation({
    mutationFn: async () => {
      if (!venueId) throw new Error('Venue ID is required');
      return vendorApi.getHeldBookings(venueId);
    },
  });
}
